import * as THREE from 'three';
import { Water } from 'three/examples/jsm/objects/Water.js';
import waterParticleTexture from '../../assets/images/img/waterParticleTexture.jpg';
import store from '../../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './animationControls';
import { setLifeTimeout, clearLifeTimeout } from './lifeUtils';

// Map intensity (string 'Low'|'Medium'|'High' or numeric) to an opacity value
const parseIntensityToOpacity = (intensity, defaultOpacity = 0.8) => {
  if (intensity === undefined || intensity === null) return defaultOpacity;
  if (typeof intensity === 'number') return Math.max(0, Math.min(1, intensity));
  const s = String(intensity).trim().toLowerCase();
  if (s === 'low') return 0.2;
  if (s === 'medium') return 0.5;
  if (s === 'high') return 0.8;
  const n = parseFloat(s);
  return !isNaN(n) ? Math.max(0, Math.min(1, n)) : defaultOpacity;
};

const getPrimarySunDirection = (scene) => {
  if (!scene) return new THREE.Vector3(0.5, 1, 0.3).normalize();
  const directional = scene.children.find((obj) => obj?.isDirectionalLight);
  if (!directional) return new THREE.Vector3(0.5, 1, 0.3).normalize();

  const dir = new THREE.Vector3();
  if (directional.target) {
    const from = directional.getWorldPosition(new THREE.Vector3());
    const to = directional.target.getWorldPosition(new THREE.Vector3());
    dir.copy(to).sub(from);
  } else {
    directional.getWorldDirection(dir);
  }
  if (dir.lengthSq() === 0) return new THREE.Vector3(0.5, 1, 0.3).normalize();
  return dir.normalize();
};

const handleWater = (scene, particle, renderer) => {
  if (!scene) return;

  // Dynamic properties with natural defaults
  const waterSize = particle?.waterSize || 5;
  const range = particle?.range !== undefined ? Number(particle.range) : 1;
  const color = particle?.color || '#006994';
  const _intensity = particle?.intensity ?? particle?.intencity;
  const opacity = parseIntensityToOpacity(_intensity, particle?.opacity ?? 0.8);
  const roughness = particle?.roughness || 0.05;
  const metalness = particle?.metalness || 0.95;
  const waveSpeed = particle?.waveSpeed || 0.012;
  const waveHeight = particle?.waveHeight || 0.8;
  const waveFrequency = particle?.waveFrequency || 0.015;
  const foamOpacity = particle?.foamOpacity || 0.3;
  const causticsIntensity = particle?.causticsIntensity || 0.6;
  const depthFade = particle?.depthFade || 0.4;
  const distortionScale =
    particle?.distortionScale !== undefined
      ? Number(particle.distortionScale)
      : particle?.reflectionBlur !== undefined
        ? Number(particle.reflectionBlur)
        : 3.7;
  

  // Remove existing water
  const existingWater = scene.getObjectByName('water');
  if (existingWater) scene.remove(existingWater);

  // Create water group
  const waterGroup = new THREE.Group();
  const life = particle?.life || 120;
  waterGroup.deleteType = 'particle';
  waterGroup.objType = 'water';

  // Unique ID - use for internal reference
  const uniqueId = particle?.id || particle?._id || `water_${Date.now()}`;
  waterGroup.name = uniqueId;
  waterGroup.displayName = 'Water'; // For UI display
  waterGroup.url = uniqueId;
  waterGroup.asset_file_name = 'water';
  waterGroup.file_name = 'water';
  waterGroup.mesh_code = 'water';
  waterGroup.glb_type = 'shapes';
  waterGroup.createdAt = new Date().toISOString();

  // === THREE.JS WATER SHADER PLANE (ONLY LAYER) ===
  const waterGeometry = new THREE.PlaneGeometry(waterSize * range, waterSize * range);
  const waterNormalTexture = new THREE.TextureLoader().load(
    waterParticleTexture
  );
  waterNormalTexture.wrapS = waterNormalTexture.wrapT = THREE.RepeatWrapping;

  const water = new Water(waterGeometry, {
    textureWidth: 512,
    textureHeight: 512,
    waterNormals: waterNormalTexture,
    sunDirection: getPrimarySunDirection(scene),
    sunColor: 0xffffff,
    waterColor: color,
    distortionScale: distortionScale,
    clipBias: 0.003,
    fog: scene.fog !== undefined,
    side: THREE.DoubleSide,
    alpha: opacity,
  });

  water.rotation.x = -Math.PI / 2;
  water.position.y = 0.1;
  
  // Apply opacity to water material
  water.material.transparent = true;
  water.material.opacity = opacity;
  
  waterGroup.add(water);

  // === ENABLE SHADOWS ===
  water.castShadow = true;
  water.receiveShadow = true;
  if (water.material) {
    water.material.shadowSide = THREE.DoubleSide;
    water.material.needsUpdate = true;
  }

  // === SET TRANSFORMS ===
  if (particle?.position) {
    waterGroup.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    waterGroup.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    waterGroup.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }

  // === USER DATA ===
  waterGroup.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: color,
      roughness: roughness,
      metalness: metalness,
    },
    waterSize,
    range,
    color,
    opacity,
    intensity: _intensity,
    roughness,
    metalness,
    waveSpeed,
    waveHeight,
    waveFrequency,
    foamOpacity,
    causticsIntensity,
    depthFade,
    distortionScale,
    life,
    lifeVariation: particle?.lifeVariation ?? 0,
  };

  scene.add(waterGroup);
  store.dispatch(setPushIntersectedObject(waterGroup));
  store.dispatch(selected_object(waterGroup));
  selectObjectInScene(waterGroup.name);

  // === ANIMATION ===
  let time = 0;
  const animate = () => {
    if (!waterGroup.parent) return;

    time += waveSpeed;

    // === THREE.JS WATER SHADER TIME ===
    if (water && water.material.uniforms) {
      water.material.uniforms['time'].value = time;
      if (water.material.uniforms['sunDirection']) {
        water.material.uniforms['sunDirection'].value.copy(
          getPrimarySunDirection(scene)
        );
      }
    }

    waterGroup.userData.animationId = requestAnimationFrame(animate);
  };

  waterGroup.userData.animateFunction = animate;

  if (!particle?.isStopped) {
    startAnimation(waterGroup, animate, waterGroup.name);
    setLifeTimeout(
      waterGroup,
      typeof life === 'number' ? life : parseFloat(life) || 0
    );
  }

  // store.dispatch(selected_object(waterGroup));
  // selectObjectInScene(waterGroup.name);
};

// === UPDATE WATER ===
const updateWater = (scene, waterSystem, newParticleData) => {
  
  if (!scene || !waterSystem || waterSystem.objType !== 'water') {
    console.warn('Invalid water system or scene');
    return;
  }

  const currentConfig = waterSystem.userData || {};
  const isPlaying =
    newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
  const isStopped =
    newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

  // === BUILD UPDATED CONFIG ===
  const updatedConfig = {
    waterSize: newParticleData?.waterSize ?? currentConfig.waterSize ?? 5,
    range: newParticleData?.range !== undefined ? Number(newParticleData.range) : (currentConfig.range ?? 1),
    color: newParticleData?.color ?? currentConfig.color ?? '#006994',
    // Opacity may come directly or via an `intensity` value (Low/Medium/High or numeric)
    opacity:
      newParticleData?.opacity !== undefined
        ? newParticleData.opacity
        : newParticleData?.intensity !== undefined
        ? parseIntensityToOpacity(newParticleData.intensity, currentConfig.opacity ?? 0.8)
        : currentConfig.opacity ?? 0.8,
    roughness: newParticleData?.roughness ?? currentConfig.roughness ?? 0.05,
    metalness: newParticleData?.metalness ?? currentConfig.metalness ?? 0.95,
    waveSpeed: newParticleData?.waveSpeed ?? currentConfig.waveSpeed ?? 0.012,
    waveHeight: newParticleData?.waveHeight ?? currentConfig.waveHeight ?? 0.8,
    waveFrequency:
      newParticleData?.waveFrequency ?? currentConfig.waveFrequency ?? 0.015,
    foamOpacity:
      newParticleData?.foamOpacity ?? currentConfig.foamOpacity ?? 0.3,
    causticsIntensity:
      newParticleData?.causticsIntensity ??
      currentConfig.causticsIntensity ??
      0.6,
    depthFade: newParticleData?.depthFade ?? currentConfig.depthFade ?? 0.4,
    distortionScale:
      newParticleData?.distortionScale !== undefined
        ? Number(newParticleData.distortionScale)
        : newParticleData?.reflectionBlur !== undefined
          ? Number(newParticleData.reflectionBlur)
          : currentConfig.distortionScale ?? 3.7,
    life: newParticleData?.life ?? currentConfig.life ?? 120,
    lifeVariation: newParticleData?.lifeVariation ?? currentConfig.lifeVariation ?? 0,
    isPlaying,
    isStopped,
  };

  
  

  const newColor = new THREE.Color(updatedConfig.color);

  // === UPDATE WATER MATERIAL ===
  const water = waterSystem.children[0]; // Only child is the water layer
  
  if (water && water.isWater) {
    // Update water color
    if (water.material.uniforms && water.material.uniforms.waterColor)
      water.material.uniforms.waterColor.value = newColor;

    // Update opacity when either `opacity` or `intensity` was provided
    if (newParticleData?.opacity !== undefined || newParticleData?.intensity !== undefined) {
      water.material.opacity = updatedConfig.opacity;
      if (water.material.uniforms && water.material.uniforms.alpha)
        water.material.uniforms.alpha.value = updatedConfig.opacity;
    }
    if (water.material.uniforms && water.material.uniforms.distortionScale) {
      water.material.uniforms.distortionScale.value = updatedConfig.distortionScale;
    }
    if (water.material.uniforms && water.material.uniforms.sunDirection) {
      water.material.uniforms.sunDirection.value.copy(getPrimarySunDirection(scene));
    }
  }

  // === UPDATE GEOMETRY IF SIZE OR RANGE CHANGED ===
  if (
    (newParticleData?.waterSize !== undefined &&
      newParticleData.waterSize !== currentConfig.waterSize) ||
    (newParticleData?.range !== undefined &&
      newParticleData.range !== currentConfig.range)
  ) {
    const newSize = updatedConfig.waterSize;
    const newRange = updatedConfig.range;

    if (water && water.geometry) {
      // Dispose old geometry
      water.geometry.dispose();
      
      // Create new geometry with updated size and range
      water.geometry = new THREE.PlaneGeometry(newSize * newRange, newSize * newRange);

      // Force TransformControls to recompute bounding box
      waterSystem.userData.forceBoxUpdate = true;

      // Re-enable shadows after geometry replacement
      water.castShadow = true;
      water.receiveShadow = true;
      if (water.material) {
        water.material.shadowSide = THREE.DoubleSide;
        water.material.needsUpdate = true;
      }
    }
  }

  // === CHECK IF WAVE ANIMATION NEEDS TO BE RECREATED ===
  const waveParamsChanged =
    newParticleData?.waveSpeed !== undefined ||
    newParticleData?.waveHeight !== undefined ||
    newParticleData?.waveFrequency !== undefined;

  // === UPDATE USERDATA FIRST (before recreating animation) ===
  waterSystem.userData = {
    ...waterSystem.userData,
    ...updatedConfig,
    intensity: newParticleData?.intensity ?? waterSystem.userData?.intensity,
    material_settings: {
      ...waterSystem.userData.material_settings,
      shader: 'Standard',
      albedo: updatedConfig.color,
      roughness: updatedConfig.roughness,
      metalness: updatedConfig.metalness,
    },
  };

  // === RECREATE ANIMATION IF WAVE PARAMETERS CHANGED ===
  if (waveParamsChanged) {
    

    // Stop existing animation
    stopAnimation(waterSystem, waterSystem.name);

    // Create new animation function with updated parameters
    let time = 0;
    const animate = () => {
      if (!waterSystem.parent) return;

      // Read parameters from userData for dynamic updates
      const { waveSpeed } = waterSystem.userData;

      time += waveSpeed;

      // === THREE.JS WATER SHADER TIME ===
      if (water && water.material.uniforms) {
        water.material.uniforms['time'].value = time;
      }

      waterSystem.userData.animationId = requestAnimationFrame(animate);
    };

    // Store the new animation function
    waterSystem.userData.animateFunction = animate;

    // Start animation if not stopped
    if (!isStopped && isPlaying) {
      startAnimation(waterSystem, animate, waterSystem.name);
    }
  } else {
    // === ANIMATION CONTROL (if wave params didn't change) ===
    if (isStopped) {
      stopAnimation(waterSystem, waterSystem.name);
    } else if (isPlaying) {
      const fn = waterSystem.userData.animateFunction;
      if (fn) startAnimation(waterSystem, fn, waterSystem.name);
    } else {
      pauseAnimation(waterSystem, waterSystem.name);
    }
  }

  // === UPDATE TRANSFORMS ===
  if (newParticleData?.position) {
    waterSystem.position.set(
      newParticleData.position.x ?? waterSystem.position.x,
      newParticleData.position.y ?? waterSystem.position.y,
      newParticleData.position.z ?? waterSystem.position.z
    );
  }
  if (newParticleData?.rotation) {
    waterSystem.rotation.set(
      newParticleData.rotation.x ?? waterSystem.rotation.x,
      newParticleData.rotation.y ?? waterSystem.rotation.y,
      newParticleData.rotation.z ?? waterSystem.rotation.z
    );
  }
  
  if (newParticleData?.scale) {
    waterSystem.scale.set(
      newParticleData.scale.x ?? waterSystem.scale.x,
      newParticleData.scale.y ?? waterSystem.scale.y,
      newParticleData.scale.z ?? waterSystem.scale.z
    );
  }

  // === UPDATE ID/NAME ===
  if (newParticleData?.id && newParticleData.id !== waterSystem.name) {
    waterSystem.name = newParticleData.id;
    waterSystem.url = newParticleData.id;
  }

  // === DISPATCH TO STORE ===
  store.dispatch(setPushIntersectedObject(waterSystem));
  // store.dispatch(selected_object(waterSystem));

  // Manage life timeout: clear previous then set new if provided
  try {
    clearLifeTimeout(waterSystem);
    const lifeVal = updatedConfig?.life ?? waterSystem.userData?.life;
    const lifeSeconds =
      typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal);
    if (!isNaN(lifeSeconds) && lifeSeconds > 0) {
      setLifeTimeout(waterSystem, lifeSeconds);
      waterSystem.visible = true;
      if (waterSystem.userData) {
        waterSystem.userData.isStopped = false;
        waterSystem.userData.isPlaying = true;
      }
    }
  } catch (e) {
    // ignore
  }

  return waterSystem;
};

const findWaterSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'water'
  );
};

const updateWaterByName = (scene, nameOrId, newParticleData) => {
  const waterSystem = findWaterSystem(scene, nameOrId);
  return waterSystem ? updateWater(scene, waterSystem, newParticleData) : null;
};

export default handleWater;
export { updateWater, updateWaterByName };